import { useLocation, useNavigate } from 'react-router-dom'
import { Headset, LogOut, Menu, Send, User } from 'lucide-react'
import { useState } from 'react'
import { SupportRequestModal } from '../../components/SupportRequestModal'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheetMobileMenu'
import { useAuth } from '../../../app/hooks/useAuth'
import { cn } from '../../../app/utils/cn'
import { BasicAvatar } from '../../components/BasicAvatar'
import { UserRoleDicionary } from '../../../app/services/usersService/user'
import { Calendar } from 'iconsax-react'

interface MobileMenuProps {
  menus: {
    label: string
    path: string
    icon: React.ReactNode
    isActive?: boolean
    count?: number
  }[]
  onNotificationClick: () => void
  handleImportData: () => void
}

export const MobileMenu = ({
  menus,
  onNotificationClick,
  handleImportData,
}: MobileMenuProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false)

  const locationPath = location.pathname

  const { user, isSuperAdmin, isAdmin, isSupport, signout } = useAuth()

  const isSupportStaff = isSuperAdmin || isAdmin || isSupport

  const handleLogOut = () => {
    signout()
    navigate('/')
  }

  const handleMenuClick = (path: string) => {
    setIsOpen(false)
    navigate(path)
  }

  const handleOpenSupportRequest = () => {
    setIsOpen(false)
    setIsSupportModalOpen(true)
  }

  return (
    <div className="h-full xs:h-screen">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger className="h-full">
          <Menu />
          <div className="hidden xs:block xs:h-full">
            <div className="mt-6 flex h-full flex-col justify-between pb-20">
              <ul className="flex flex-col gap-4 font-semibold">
                {menus.map(({ icon, path }) => (
                  <button key={path} onClick={() => handleMenuClick(path)}>
                    <li
                      className={cn(
                        '-ml-2 p-2',
                        locationPath.startsWith(path)
                          ? 'rounded-xl bg-primary text-white'
                          : 'text-black04',
                      )}
                    >
                      {icon}
                    </li>
                  </button>
                ))}
              </ul>
              <ul className="space-y-8">
                {!isSupportStaff && (
                  <li className="cursor-pointer">
                    <button
                      title="Abrir chamado"
                      aria-label="Abrir chamado"
                      onClick={(event) => {
                        event.stopPropagation()
                        handleOpenSupportRequest()
                      }}
                    >
                      <Headset />
                    </button>
                  </li>
                )}
                <li className="cursor-pointer">
                  <button
                    onClick={() => handleMenuClick(`/perfil/${user?._id}`)}
                  >
                    <User />
                  </button>
                </li>
                <li onClick={handleLogOut} className="cursor-pointer gap-2">
                  <LogOut />
                </li>
              </ul>
            </div>
          </div>
        </SheetTrigger>
        <SheetContent side="left" className="w-full overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-start gap-2">
              <BasicAvatar
                name={user?.name}
                profileImageUrl={user?.profileImageUrl}
              />
              <div className="ml-2 mr-4">
                <p className="font-semibold">{user?.name}</p>
                <p className="text-[10px] font-medium text-black04">
                  {user?.role ? UserRoleDicionary[user.role] : ''}
                </p>
              </div>
            </SheetTitle>
          </SheetHeader>
          <div className="w-full border-b-[1px] border-gray01 py-3" />
          <div className="mt-6 flex h-[90%] flex-col justify-between">
            <ul className="flex flex-col gap-4 font-semibold">
              {menus.map(({ icon, label, path, count, isActive }) => (
                <button
                  key={path}
                  onClick={() => handleMenuClick(path)}
                  className="text-left"
                >
                  <li
                    className={cn(
                      'flex h-11 w-52 items-center gap-2 px-6 py-2',
                      isActive &&
                        'border-2 border-dashed border-primary text-primary',
                      locationPath.startsWith(path)
                        ? 'rounded-full bg-primary text-white'
                        : 'text-black04',
                    )}
                  >
                    {icon}
                    <p>{label}</p>

                    {count && (
                      <span
                        className={cn(
                          'ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white',
                        )}
                      >
                        {count > 99 ? '+99' : count}
                      </span>
                    )}
                  </li>
                </button>
              ))}
            </ul>
            <ul className="space-y-3">
              {isSuperAdmin && (
                <li
                  className="mt-3 cursor-pointer p-4"
                  onClick={() => {
                    onNotificationClick()
                    setIsOpen(false)
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Send />
                    <p className="text-sm font-medium">Enviar notificação</p>
                  </div>
                </li>
              )}

              {isSuperAdmin && (
                <li
                  className="mt-3 cursor-pointer p-4"
                  onClick={() => {
                    handleImportData()
                    setIsOpen(false)
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Calendar />
                    <p className="text-sm font-medium">Agendar consulta CPFs</p>
                  </div>
                </li>
              )}

              {!isSupportStaff && (
                <li className="cursor-pointer p-4">
                  <button
                    className="flex items-center gap-2"
                    onClick={handleOpenSupportRequest}
                  >
                    <Headset />
                    <p className="text-sm font-medium">Abrir chamado</p>
                  </button>
                </li>
              )}

              <li className="cursor-pointer p-4">
                <button
                  className="flex items-center gap-2"
                  onClick={() => handleMenuClick(`/perfil/${user?._id}`)}
                >
                  <User />
                  <p className="text-sm font-medium">Editar perfil</p>
                </button>
              </li>
              <li
                onClick={handleLogOut}
                className="flex cursor-pointer gap-2 p-4"
              >
                <LogOut />
                <p className="text-sm font-medium">Sair</p>
              </li>
            </ul>
          </div>
        </SheetContent>
      </Sheet>

      <SupportRequestModal
        open={isSupportModalOpen}
        onOpenChange={setIsSupportModalOpen}
      />
    </div>
  )
}
